class KoraBundleSelector extends HTMLElement {
  connectedCallback() {
    this.currentProductId = this.normalizeId(this.dataset.currentProductId);
    this.currentVariantId = this.normalizeId(this.dataset.currentVariantId);
    this.currentAvailable = this.dataset.currentAvailable === 'true';
    this.currentPrice = Number(this.dataset.currentPrice || 0);
    try { this.currentVariants = JSON.parse(this.querySelector('[data-current-product-variants]')?.textContent || '[]'); }
    catch (error) { this.currentVariants = []; console.error('Kora bundle: varianti del prodotto corrente non valide.', error); }
    this.updateCurrentSummary();
    this.panels = [...this.querySelectorAll('[data-bundle-panel]')];
    this.choices = [...this.querySelectorAll('[data-bundle-choice]')];
    this.states = new Map();
    for (const panel of this.panels) {
      const state = this.createState(panel);
      this.states.set(panel.id, state);
      this.initializeState(state);
    }
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    document.addEventListener('shopify:product:select', this.onProductSelect);
    document.addEventListener('change', this.onDocumentChange, true);
    const firstPanel = this.panels.find((panel) => !panel.hidden) || this.panels[0];
    if (firstPanel) this.activate(firstPanel.id);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    document.removeEventListener('shopify:product:select', this.onProductSelect);
    document.removeEventListener('change', this.onDocumentChange, true);
  }

  createState(panel) {
    let products = [];
    let nativeVariants = [];
    try { products = JSON.parse(panel.querySelector('[data-bundle-products]')?.textContent || '[]'); }
    catch (error) { console.error('Kora bundle: configurazione prodotti non valida.', error); }
    try {
      const nativeVariantMap = JSON.parse(panel.querySelector('[data-native-bundle-variants]')?.textContent || '{}');
      nativeVariants = nativeVariantMap.version === 1 && Array.isArray(nativeVariantMap.variants)
        ? nativeVariantMap.variants
        : [];
    }
    catch (error) { console.error('Kora bundle: mappa varianti native non valida.', error); }
    return {
      panel, products, nativeVariants,
      productSelect: panel.querySelector('[data-companion-product]'),
      variantSelect: panel.querySelector('[data-companion-variant]'),
      variantLabel: panel.querySelector('[data-companion-variant]')?.closest('label'),
      title: panel.querySelector('[data-companion-title]'),
      image: panel.querySelector('[data-companion-image]'),
      originalPrice: panel.querySelector('[data-bundle-original-price]'),
      finalPrice: panel.querySelector('[data-bundle-final-price]'),
      saving: panel.querySelector('[data-bundle-saving]'),
      summary: panel.querySelector('[data-bundle-summary]'),
      discount: Math.min(100, Math.max(0, Number(panel.dataset.bundleDiscount || 0))),
      button: panel.querySelector('[data-bundle-submit]'),
      status: panel.querySelector('[data-bundle-status]')
    };
  }

  initializeState(state) {
    if (!state.products.length || !state.productSelect || !state.variantSelect) {
      this.setStatus(state, 'Configurazione bundle incompleta.');
      if (state.button) state.button.disabled = true;
      return;
    }
    const selectedId = this.normalizeId(state.productSelect.value);
    const product = state.products.find((item) => this.normalizeId(item.id) === selectedId) || state.products[0];
    state.productSelect.value = String(product.id);
    this.renderProduct(state, product);
  }

  onClick = async (event) => {
    const confirm = event.target.closest('[data-bundle-confirm]');
    if (confirm && this.contains(confirm)) {
      const state = this.pendingState;
      this.querySelector('[data-bundle-confirm-dialog]')?.close();
      this.pendingState = null;
      if (state) await this.addBundle(state);
      return;
    }
    const cancel = event.target.closest('[data-bundle-cancel]');
    if (cancel && this.contains(cancel)) {
      this.querySelector('[data-bundle-confirm-dialog]')?.close();
      this.pendingState = null;
      return;
    }
    const zoom = event.target.closest('[data-bundle-image-zoom]');
    if (zoom && this.contains(zoom)) {
      const dialog = this.querySelector('#' + CSS.escape(zoom.getAttribute('aria-controls')));
      if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
      return;
    }
    const close = event.target.closest('[data-bundle-image-close]');
    if (close && this.contains(close)) { close.closest('dialog')?.close(); return; }
    if (event.target instanceof HTMLDialogElement && event.target.matches('[data-bundle-image-dialog]')) {
      event.target.close(); return;
    }
    const currentProductJump = event.target.closest('[data-current-product-jump]');
    if (currentProductJump && this.contains(currentProductJump)) {
      this.jumpToProductSelector();
      return;
    }
    const choice = event.target.closest('[data-bundle-choice]');
    if (choice && this.contains(choice)) { this.activate(choice.dataset.panelId); return; }
    const button = event.target.closest('[data-bundle-submit]');
    if (!button || !this.contains(button)) return;
    const panel = button.closest('[data-bundle-panel]');
    const state = panel ? this.states.get(panel.id) : null;
    if (state) this.openConfirmation(state);
  };

  onChange = (event) => {
    const panel = event.target.closest('[data-bundle-panel]');
    const state = panel ? this.states.get(panel.id) : null;
    if (!state) return;
    if (event.target.matches('[data-companion-product]')) {
      const product = state.products.find((item) => this.normalizeId(item.id) === this.normalizeId(event.target.value));
      if (product) this.renderProduct(state, product);
    }
    if (event.target.matches('[data-companion-variant]')) this.renderVariant(state);
  };

  onProductSelect = (event) => {
    if (this.normalizeId(event.product?.id) !== this.currentProductId) return;
    const variant = event.variant || event.productVariant || event.selectedVariant;
    if (variant?.id) {
      this.currentVariantId = this.normalizeId(variant.id);
      this.currentAvailable = variant.available !== false;
      const storedVariant = this.currentVariants.find((item) => this.normalizeId(item.id) === this.currentVariantId);
      this.currentPrice = Number(variant.price ?? storedVariant?.price ?? this.currentPrice);
      this.updateCurrentSummary();
      this.refreshButtons();
    } else window.setTimeout(() => this.readCurrentVariantFromPage(), 0);
  };

  onDocumentChange = (event) => {
    if (!this.contains(event.target)) window.setTimeout(() => this.readCurrentVariantFromPage(), 0);
  };

  readCurrentVariantFromPage() {
    const component = [...document.querySelectorAll('product-form-component[data-product-id]')]
      .find((item) => this.normalizeId(item.dataset.productId) === this.currentProductId);
    const input = component?.querySelector('input[name="id"]');
    if (input?.value) {
      this.currentVariantId = this.normalizeId(input.value);
      this.currentAvailable = !input.disabled;
      const storedVariant = this.currentVariants.find((item) => this.normalizeId(item.id) === this.currentVariantId);
      if (storedVariant) this.currentPrice = Number(storedVariant.price || 0);
      this.updateCurrentSummary();
      this.refreshButtons();
    }
  }

  jumpToProductSelector() {
    const scope = this.closest('.shopify-section') || document;
    const selectorGroups = [...scope.querySelectorAll('variant-picker, variant-selects, [data-variant-picker], .variant-picker')]
      .filter((item) => !this.contains(item) && item.getClientRects().length);
    let target = selectorGroups[0];

    if (!target) {
      target = [...scope.querySelectorAll('select')]
        .find((item) => !this.contains(item) && item.getClientRects().length);
    }
    if (!target) target = scope.querySelector('h1, [data-product-title]');
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusTarget = target.matches('select, input, button')
      ? target
      : target.querySelector('select, input:not([type="hidden"]), button');
    if (focusTarget) window.setTimeout(() => focusTarget.focus({ preventScroll: true }), 450);
  }

  activate(panelId) {
    for (const state of this.states.values()) state.panel.hidden = state.panel.id !== panelId;
    for (const choice of this.choices) choice.setAttribute('aria-pressed', String(choice.dataset.panelId === panelId));
  }

  renderProduct(state, product) {
    if (!product) return;
    if (state.title) state.title.textContent = product.title || '';
    const variants = product.variants || [];
    const hasVariantChoice = variants.length > 1;
    if (state.variantLabel) state.variantLabel.hidden = !hasVariantChoice;
    state.variantSelect.replaceChildren();
    for (const variant of variants) {
      const option = document.createElement('option');
      option.value = this.normalizeId(variant.id);
      option.textContent = variant.title === 'Default Title' ? 'Variante unica' : variant.title;
      option.disabled = variant.available === false;
      state.variantSelect.append(option);
    }
    const firstAvailable = [...state.variantSelect.options].find((option) => !option.disabled);
    if (firstAvailable) state.variantSelect.value = firstAvailable.value;
    this.renderVariant(state);
  }

  updateCurrentSummary() {
    const variant = this.currentVariants?.find((item) => this.normalizeId(item.id) === this.currentVariantId);
    const label = !variant || variant.title === 'Default Title' ? 'Variante unica' : variant.title;
    for (const target of this.querySelectorAll('[data-current-variant-title]')) target.textContent = label;
  }

  renderVariant(state) {
    const product = state.products.find((item) => this.normalizeId(item.id) === this.normalizeId(state.productSelect.value)) || state.products[0];
    const variant = product?.variants?.find((item) => this.normalizeId(item.id) === this.normalizeId(state.variantSelect.value));
    const variantImage = variant?.featured_media?.preview_image?.src || variant?.featured_image?.src || product?.image || '';
    if (state.image) {
      state.image.hidden = !variantImage;
      if (variantImage) {
        state.image.src = variantImage;
        state.image.alt = variant?.title && variant.title !== 'Default Title'
          ? product.title + ' - ' + variant.title
          : product.title || '';
      }
    }
    const nativeVariant = variant ? this.findNativeVariant(state, product, variant) : null;
    const originalTotal = nativeVariant ? Number(nativeVariant.compareAtPrice || 0) : 0;
    const finalTotal = nativeVariant ? Number(nativeVariant.price || 0) : 0;
    const saving = originalTotal - finalTotal;
    const discount = originalTotal > 0 ? Math.round((saving / originalTotal) * 100) : 0;
    if (state.originalPrice) {
      state.originalPrice.hidden = discount <= 0 || !nativeVariant;
      state.originalPrice.textContent = nativeVariant ? this.money(originalTotal) : '';
    }
    if (state.finalPrice) state.finalPrice.textContent = nativeVariant ? this.money(finalTotal) : '';
    if (state.saving) {
      state.saving.hidden = discount <= 0 || !nativeVariant;
      state.saving.textContent = nativeVariant ? 'Risparmi ' + this.money(saving) + ' (-' + discount + '%)' : '';
    }
    if (state.summary) state.summary.hidden = !nativeVariant;
    if (state.button) state.button.disabled = !this.currentAvailable || !nativeVariant || nativeVariant.available === false;
    this.setStatus(state, nativeVariant ? '' : 'Combinazione bundle non disponibile.');
  }

  refreshButtons() { for (const state of this.states.values()) this.renderVariant(state); }

  openConfirmation(state) {
    const product = state.products.find((item) => this.normalizeId(item.id) === this.normalizeId(state.productSelect?.value)) || state.products[0];
    const variant = product?.variants?.find((item) => this.normalizeId(item.id) === this.normalizeId(state.variantSelect?.value));
    const nativeVariant = product && variant ? this.findNativeVariant(state, product, variant) : null;
    if (!product || !variant || !this.currentVariantId || !nativeVariant) {
      this.setStatus(state, 'Selezione bundle non valida.');
      return;
    }
    const currentProduct = this.querySelector('[data-current-product-title]')?.textContent?.trim() || 'Primo gioiello';
    const currentVariant = this.querySelector('[data-current-variant-title]')?.textContent?.trim() || '';
    const companionVariant = state.variantSelect?.selectedOptions?.[0]?.textContent?.trim() || '';
    const currentProductTarget = this.querySelector('[data-bundle-confirm-current-product]');
    const currentVariantTarget = this.querySelector('[data-bundle-confirm-current-variant]');
    const companionProductTarget = this.querySelector('[data-bundle-confirm-companion-product]');
    const companionVariantTarget = this.querySelector('[data-bundle-confirm-companion-variant]');
    if (currentProductTarget) currentProductTarget.textContent = currentProduct;
    if (currentVariantTarget) {
      currentVariantTarget.textContent = currentVariant === 'Variante unica' ? 'Variante unica' : currentVariant;
    }
    if (companionProductTarget) companionProductTarget.textContent = product.title;
    if (companionVariantTarget) companionVariantTarget.textContent = companionVariant || 'Variante unica';
    this.pendingState = state;
    const dialog = this.querySelector('[data-bundle-confirm-dialog]');
    if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
  }

  async addBundle(state) {
    const product = state.products.find((item) => this.normalizeId(item.id) === this.normalizeId(state.productSelect?.value)) || state.products[0];
    const variant = product?.variants?.find((item) => this.normalizeId(item.id) === this.normalizeId(state.variantSelect?.value));
    const nativeVariant = product && variant ? this.findNativeVariant(state, product, variant) : null;
    if (!this.currentVariantId || !nativeVariant?.id) {
      this.setStatus(state, 'Selezione bundle non valida.'); return;
    }
    state.button.disabled = true;
    this.setStatus(state, 'Aggiunta in corso…');
    const properties = {
      '_Bundle': state.panel.dataset.bundleTitle || 'Bundle',
      '_BundleId': state.panel.dataset.bundleId || '',
      '_BundleType': state.panel.dataset.bundleType || '',
      '_BundleDiscount': Math.round(state.discount) + '%'
    };
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [
          { id: Number(this.normalizeId(nativeVariant.id)), quantity: 1, properties }
        ]})
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Errore carrello');
      window.location.assign(this.dataset.cartUrl || '/cart');
    } catch (error) {
      console.error('Kora bundle: aggiunta al carrello non riuscita.', error);
      this.setStatus(state, 'Impossibile aggiungere il bundle. Controlla le selezioni e riprova.');
      this.renderVariant(state);
    }
  }

  findNativeVariant(state, companionProduct, companionVariant) {
    if (!state.nativeVariants?.length || !this.currentVariantId || !companionVariant?.id) return null;

    const selectedVariantIds = [
      this.normalizeId(this.currentVariantId),
      this.normalizeId(companionVariant.id)
    ].sort();

    return state.nativeVariants.find((item) => {
      const componentVariantIds = (item.components || [])
        .map((component) => this.normalizeId(component.variantId))
        .filter(Boolean)
        .sort();

      return componentVariantIds.length === selectedVariantIds.length
        && componentVariantIds.every((id, index) => id === selectedVariantIds[index]);
    }) || null;
  }

  setStatus(state, message) { if (state.status) state.status.textContent = message; }
  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
      style: 'currency', currency: window.Shopify?.currency?.active || 'EUR'
    }).format(Number(cents || 0) / 100);
  }
  normalizeId(value) { return String(value || '').split('/').pop(); }
}
if (!customElements.get('kora-bundle-selector')) customElements.define('kora-bundle-selector', KoraBundleSelector);
